import { isObject, isPresent } from '@apl-digital/utils';
import { z } from 'zod';
import dlv from 'dlv';
import { deepEqual } from 'fast-equals';

class TenantConfigException extends Error {
    print(configPath) {
        switch(this.payload.type){
            case 'json-parse':
                {
                    TenantConfigException.printZodParseErrors(this.payload.errors, configPath);
                    break;
                }
            case 'general':
                {
                    console.log(`[Tenant Config] %c${configPath}%c: ${this.payload.error}`, 'color: teal', 'color: notset');
                    break;
                }
            case 'invalid-type':
                {
                    console.log(`[Tenant Config] %c${configPath}%c: Expected %o, but got %o`, 'color: teal', 'color: notset', this.payload.expected, this.payload.received);
                    break;
                }
            case 'site-config-parse':
                {
                    TenantConfigException.printZodParseErrors(this.payload.errors, configPath);
                    break;
                }
            case 'no-config-locations':
                {
                    console.log('[Tenant Config] %s', this.payload.message);
                    break;
                }
            case 'no-applicable-configs':
                {
                    console.log('[Tenant Config] %s', this.payload.message);
                    break;
                }
            case 'no-config-path':
                {
                    console.log('[Tenant Config] %s', this.payload.message);
                    break;
                }
        }
    }
    constructor(payload){
        super('an error occurred while parsing the tenant config');
        this.payload = payload;
        this.name = 'TenantConfigException';
    }
}
TenantConfigException.printZodParseErrors = (error, pathPrefix = '@/')=>{
    for (const issue of error.error.errors){
        const path = [
            pathPrefix,
            issue.path
        ].join('.');
        switch(issue.code){
            case 'invalid_type':
            case 'invalid_literal':
                console.log(`[Tenant Config] %c${path}%c: Expected %o, but got %o`, 'color: teal', 'color: notset', issue.expected, issue.received);
                break;
            case 'unrecognized_keys':
                console.log(`[Tenant Config] %c${path}%c: Unrecognized keys: %o`, 'color: teal', 'color: notset', issue.keys);
                break;
            default:
                console.log(`[Tenant Config] %c${path}%c: ${issue.message}`, 'color: teal', 'color: notset');
        }
    }
};

const literalSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null()
]);
const jsonSchema = z.lazy(()=>z.union([
        literalSchema,
        z.array(jsonSchema),
        z.record(jsonSchema)
    ]));

const json = (json)=>{
    const parsedJson = jsonSchema.safeParse(json);
    if (!parsedJson.success) {
        throw new TenantConfigException({
            type: 'json-parse',
            message: 'Failed to parse JSON',
            errors: parsedJson
        });
    }
    return parsedJson.data;
};
const siteConfigs = (rawConfig)=>{
    const data = json(rawConfig);
    if (!isObject(data)) {
        throw new TenantConfigException({
            type: 'invalid-type',
            expected: 'object',
            received: data
        });
    }
    const siteConfigs = dlv(data, 'siteConfigs');
    if (!isObject(siteConfigs)) {
        throw new TenantConfigException({
            type: 'invalid-type',
            expected: 'object',
            received: siteConfigs
        });
    }
    return siteConfigs;
};
const siteConfig = (schema, config)=>{
    const parsedSiteConfig = schema.safeParse(config);
    if (!parsedSiteConfig.success) {
        throw new TenantConfigException({
            type: 'site-config-parse',
            message: 'Failed to parse site config',
            errors: parsedSiteConfig
        });
    }
    return parsedSiteConfig.data;
};
const parse = {
    json,
    siteConfigs,
    siteConfig
};

const getConfigLocations = (node)=>{
    function* helper(currentNode, prev = []) {
        for (const [key, value] of Object.entries(currentNode)){
            if (key === 'config') {
                yield prev;
            } else if (isPresent(value)) {
                yield* helper(value, prev.concat(key));
            }
        }
    }
    const results = [];
    for (const i of helper(node)){
        results.push(i);
    }
    return results;
};
const findApplicableConfigs = (configLocations, requestedConfigLocation)=>{
    function* helper(path) {
        for (const configLocation of configLocations){
            if (deepEqual(configLocation, path)) {
                yield configLocation;
            }
        }
        if (path.length > 0) {
            yield* helper(path.slice(0, -1));
        }
    }
    const results = [];
    for (const i of helper(requestedConfigLocation)){
        results.push(i);
    }
    return results;
};
const pickConfigLocation = (configLocations)=>{
    // Choose the first applicable config, because its the most specific match
    const configPath = configLocations.at(0);
    if (!isPresent(configPath)) {
        throw new TenantConfigException({
            type: 'no-config-locations',
            message: 'Unable to pick a config location from an empty array.'
        });
    }
    return configPath;
};

class TenantConfig {
    setTenantKey(tenantKey) {
        this.tenantKey = tenantKey;
        const applicableConfigs = findApplicableConfigs(this.configLocations, tenantKey);
        if (applicableConfigs.length === 0) {
            throw new TenantConfigException({
                type: 'no-applicable-configs',
                message: `No applicable configs found for ${tenantKey.join('.')}, please check siteConfigs in config.yaml`
            });
        }
        this.applicableConfigs = applicableConfigs;
        this.configPath = pickConfigLocation(applicableConfigs).join('.');
    }
    getConfig() {
        if (!isPresent(this.configPath)) {
            throw new TenantConfigException({
                type: 'no-config-path',
                message: 'No config path set, please call setDomainLevels first'
            });
        }
        const tenantConfig = this.tenantConfigs[this.configPath];
        if (!isPresent(tenantConfig)) {
            throw new TenantConfigException({
                type: 'no-config-path',
                message: 'No config found at the specified config path'
            });
        }
        return tenantConfig;
    }
    findConfigForDomainLevels(domainLevels) {
        return this.tenantConfigs[domainLevels.join('.')];
    }
    findApplicableConfigs(domainLevels) {
        return findApplicableConfigs(this.configLocations, domainLevels);
    }
    printValidationResults() {
        if (Object.keys(this.validationErrors).length === 0) {
            console.groupCollapsed('[Tenant Config] ✅ %cConfig validation passed', 'color: green');
        } else {
            console.groupCollapsed('[Tenant Config] ❌ %cConfig validation failed', 'color: red');
        }
        for (const configLocation of this.configLocations){
            const configPath = configLocation.join('.');
            const name = configPath === '' ? 'Global' : configPath;
            const errors = this.validationErrors[configPath];
            if (isPresent(errors)) {
                console.groupCollapsed(`[Tenant Config] ❌ %c${name}`, 'color: teal');
                for (const error of errors){
                    error.print(configPath);
                }
                console.groupEnd();
            } else {
                console.log(`[Tenant Config] ✅ %c${name}`, 'color: teal');
            }
        }
        console.groupEnd();
    }
    constructor(schema, rawConfig){
        this.schema = schema;
        this.rawConfig = rawConfig;
        this.tenantConfigs = {};
        this.validationErrors = {};
        this.tenantKey = undefined;
        this.applicableConfigs = [];
        this.configPath = undefined;
        this.rawTenantConfigs = parse.siteConfigs(rawConfig);
        this.configLocations = getConfigLocations(this.rawTenantConfigs);
        if (this.configLocations.length === 0) {
            throw new TenantConfigException({
                type: 'no-config-locations',
                message: 'No config locations found, please check siteConfigs in config.yaml'
            });
        }
        for (const configLocation of this.configLocations){
            const configPath = configLocation.join('.');
            const rawSiteConfig = dlv(this.rawTenantConfigs, configLocation.concat('config').join('.'));
            try {
                this.tenantConfigs[configPath] = parse.siteConfig(schema, rawSiteConfig);
            } catch (error) {
                if (error instanceof TenantConfigException) {
                    if (!isPresent(this.validationErrors[configPath])) {
                        this.validationErrors[configPath] = [];
                    }
                    this.validationErrors[configPath].push(error);
                }
            }
        }
    }
}

export { TenantConfig };
